/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/no-array-index-key */
import React, {
    FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
    Button, Checkbox, Icon, Loader, Modal, notification, Title,
} from 'ui-library';
import {
    ICON_ALERT_CIRCLE_SOLID,
    ICON_CHECK_CIRCLE_SOLID,
    ICON_DOCUMENT_LOADING,
    ICON_DOCUMENT_OUTLINE,
    ICON_DOCUMENT_RE_UPLOAD,
    ICON_DOWNLOAD_OUTLINE,
    ICON_PEN_LEFT,
    ICON_TRIANGLE_OUTLINE,
    ICON_TRY_AGAIN,
    ICON_TYPE_CLOSE,
} from 'ui-library/components/Icon';
import UploadDocuments from 'components/UploadDocuments/UploadDocuments';
import Error from 'components/AtomicStructure/atoms/Error';
import { BUTTON_TYPE_DANGER, BUTTON_TYPE_SECONDARY } from 'ui-library/components/Button/constants';
import {
    FILE_TYPES,
    FILE_TYPES_ALLOWED,
} from 'pages/ClientOnboardingFlow/pages/NewsPlan/UploadDocumentsPage/validation/const';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
    CaseOrderStatus, DocBoxProps, DocStatus, DocTypes,
} from './DocBox.types';
import SM from '../../services/ServiceManager';
import { downloadDmsDocument } from '../../utils/downloadDmsDocument';
import './DocBox.css';

const DocBox: FC<DocBoxProps> = ({
    status = 1,
    uploadStatus = DocStatus.ReadyToGenerate,
    documentName,
    className,
    documentId = 0,
    contactId,
    originOfAsset,
    applicationId,
    type,
    isSent,
    canESign,
    isSentCase = false,
    allowSigning = true,
    applicationExpired = false,
    isCaseDetails,
    isDocReUploadInProgress,
    is3aVB,
    isApplicationClosed = false,
    isFollowUpInvestorProfile = false,
    canReUpload = false,
    isDocGenerationInProgress = false,
    onManualSign = () => { },
    onESign = () => { },
    onDocGeneration = () => { },
    setRetryState = () => { },
    acceptDocumentSend = () => { },
    handleDocReUpload = () => { },
    onPlanDataChangeFormModalShow = () => { },
    onAdvisoryDocReUpload = () => {},
}) => {
    const {
        t,
        i18n: { language },
    } = useTranslation();
    const docName = documentName || t('ApplicationDetails.AdvisorySection.GenerateDocument');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isSentCheckValue, setSentCheckValue] = useState(isSent);

    useEffect(() => {
        setSentCheckValue(isSent);
    }, [isSent]);

    const [files, setFiles] = useState<File[]>([]);
    const [error, setError] = useState<string>('');
    const [investorProfileChecked, setInvestorProfileChecked] = useState<boolean>(false);
    const [showInvestorProfileFakeApplicationModal, setShowInvestorProfileFakeApplicationModal] = useState<boolean>(false);

    const kycProofOfAssetsDocNames = useMemo(() => ({
        798: [
            t('ApplicationDetails.AdvisorySection.POA.PensionFunds1'),
            t('ApplicationDetails.AdvisorySection.POA.PensionFunds2'),
        ],
        799: [
            t('ApplicationDetails.AdvisorySection.POA.LifeInsurance1'),
            t('ApplicationDetails.AdvisorySection.POA.LifeInsurance2'),
        ],
        800: [
            t('ApplicationDetails.AdvisorySection.POA.Inheritance1'),
            t('ApplicationDetails.AdvisorySection.POA.Inheritance2'),
            t('ApplicationDetails.AdvisorySection.POA.Inheritance3'),
            t('ApplicationDetails.AdvisorySection.POA.Inheritance4'),
        ],
        801: [
            t('ApplicationDetails.AdvisorySection.POA.Donation1'),
            t('ApplicationDetails.AdvisorySection.POA.Donation2'),
            t('ApplicationDetails.AdvisorySection.POA.Donation3'),
        ],
        802: [t('ApplicationDetails.AdvisorySection.POA.Savings1')],
        803: [
            t('ApplicationDetails.AdvisorySection.POA.RealEstateIncome1'),
            t('ApplicationDetails.AdvisorySection.POA.RealEstateIncome2'),
            t('ApplicationDetails.AdvisorySection.POA.RealEstateIncome3'),
        ],
        804: [
            t('ApplicationDetails.AdvisorySection.POA.RealEstateSale1'),
            t('ApplicationDetails.AdvisorySection.POA.RealEstateSale2'),
        ],
        805: [t('ApplicationDetails.AdvisorySection.POA.OtherOrigin1')],
    }), [language]);

    const [isDownloadInProgress, setDownloadInProgress] = useState<boolean>(false);
    const [isUploadInProgress, setUploadInProgress] = useState<boolean>(false);
    const [isESignInInProgress, setESignInProgress] = useState<boolean>(false);

    const classNames = useMemo(() => classnames(className, 'doc-box', {
        'doc-box-error': uploadStatus === DocStatus.Error,
        'doc-box-initial': [DocStatus.ReadyToGenerate, DocStatus.Disabled].includes(uploadStatus)
            || (isApplicationClosed && [DocStatus.ReadyToSign].includes(uploadStatus)),
    }), [className, uploadStatus, isApplicationClosed]);

    const disableDocumentDownload = useMemo(() => isApplicationClosed && [DocStatus.ReadyToSign].includes(uploadStatus), [isApplicationClosed, uploadStatus]);

    const handleGenerateDocument = useCallback(async () => {
        try {
            if (applicationId) await onDocGeneration({ applicationId, applicationStatus: status, is3aVB });
        } catch (err: any) {
            if (applicationId) setRetryState({ applicationId });
        }
    }, [applicationId, status, is3aVB]);

    const handleDocumentUpload = useCallback(async () => {
        if (!files.length) {
            setError(t('contactGroupDocuments.uploadDocumentsModal.noFilesSelectedError'));

            return;
        }

        setUploadInProgress(true);
        if (type === DocTypes.ID_DOCUMENT) {
            await onManualSign({
                file: files,
                contactId,
                documentId,
                type,
            });
        } else {
            await onManualSign({
                file: files?.[0],
                contactId,
                originOfAsset,
                documentId,
                type,
            });
        }
        setUploadInProgress(false);
        setShowUploadModal(false);
        if (isFollowUpInvestorProfile) {
            setShowInvestorProfileFakeApplicationModal(true);
        }
    }, [documentId, files, originOfAsset, contactId, type, onManualSign, isFollowUpInvestorProfile]);

    const handleAdvisoryDocReUpload = useCallback(async () => {
        if (!files.length) {
            setError(t('contactGroupDocuments.uploadDocumentsModal.noFilesSelectedError'));

            return;
        }

        setUploadInProgress(true);

        try {
            const respnse: any = await onAdvisoryDocReUpload({
                filename: files?.[0]?.name || '-',
                file: files?.[0],
            });

            if (respnse?.status === 204) {
                setShowUploadModal(false);
            }
        } catch (error: any) {
            notification.open({
                content: `${t('contactGroups.somethingWentWrong')} ${error?.message}`,
                type: 'error',
            });
        } finally {
            setUploadInProgress(false);
        }
    }, [files, onAdvisoryDocReUpload]);

    const uploadStatusIconType = useMemo(() => {
        switch (uploadStatus) {
            case DocStatus.ReadyToSubmit:
                return ICON_CHECK_CIRCLE_SOLID;
            case DocStatus.ReadyToUpload:
                return '';
            case DocStatus.ReadyToSign:
                return ICON_ALERT_CIRCLE_SOLID;
            case DocStatus.Error:
                return ICON_TRIANGLE_OUTLINE;
            case DocStatus.Loading:
                return ICON_DOCUMENT_LOADING;
            default:
                return '';
        }
    }, [uploadStatus]);

    const allowedFileTypes = useMemo(() => {
        switch (type) {
            case 'PROOF_OF_ASSETS':
                return FILE_TYPES_ALLOWED;

            case 'ID_DOCUMENT':
                return FILE_TYPES_ALLOWED;

            default:
                return FILE_TYPES.pdf;
        }
    }, [type]);

    const handleDocumentSign = useCallback(async () => {
        if (!canESign) {
            return;
        }
        setESignInProgress(true);
        await onESign({ documentId });
        setESignInProgress(false);
    }, [documentId, canESign]);

    const handleDownload = useCallback(async () => {
        try {
            setDownloadInProgress(true);
            const response = await SM.documents('getDocumentById', [documentId]);

            if (response?.data) {
                const { retrieveFileAsName } = response.data;

                const { newBlob } = downloadDmsDocument(
                    response.data,
                    {
                        name: retrieveFileAsName,
                        saveFile: true,
                    },
                );
                const url = URL.createObjectURL(newBlob);

                global.open(url, '_blank');
            }
        } catch (err: any) {
            setError(err);
            notification.open({
                content: `${t('contactGroups.somethingWentWrong')} ${err?.message}`,
                type: 'error',
            });
        } finally {
            setDownloadInProgress(false);
        }
    }, [documentId, language]);

    const handleReUpload = useCallback(() => {
        setShowUploadModal(true);
    }, []);

    const onCancelDocumentSend = useCallback(() => {
        setSentCheckValue(false);
    }, []);

    const onAcceptDocumentSend = useCallback(async () => {
        await acceptDocumentSend({ documentId });
        setSentCheckValue(true);
    }, [documentId]);

    const tryToAcceptDocSend = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isSent) return;

        Modal.confirm({
            title: t('documentSign.modal.documentIsNotSent.title'),
            content: t('documentSign.modal.documentIsNotSent.description'),
            okText: t('documentSign.modal.documentIsNotSent.ok'),
            cancelText: t('documentSign.modal.documentIsNotSent.cancel'),
            onCancel: onCancelDocumentSend,
            onOk: onAcceptDocumentSend,
        });
    }, [onCancelDocumentSend, onAcceptDocumentSend, isSent, isSentCheckValue]);

    const handleInvestorProfileCheckbox = useCallback(() => {
        setInvestorProfileChecked(prev => !prev);
    }, []);

    const showUploadIcon = useMemo(() => (canReUpload
            || ([DocStatus.ReadyToUpload, DocStatus.ReadyToSign]?.includes(uploadStatus) && typeof isSent !== 'boolean')
    ), [uploadStatus, canReUpload, isSent]);

    if (applicationExpired && (uploadStatus === DocStatus.ReadyToGenerate
        || uploadStatus === DocStatus.ReadyToSign
        || uploadStatus === DocStatus.ReadyToSubmit)) {
        return (<></>);
    }

    return (
        <>
            <div className="doc-wrapper">
                <div className={classNames}>
                    <div className="doc-box-inner">
                        {uploadStatus !== DocStatus.ReadyToGenerate
                            && (
                                <Icon
                                    className={uploadStatus === 'loading' ? 'loading-spinner' : ''}
                                    type={uploadStatusIconType}
                                    size={28}
                                />
                            )}
                        {!['loading', 'error']?.includes(uploadStatus)
                            && <Icon type={ICON_DOCUMENT_OUTLINE} size={28} />}
                        {uploadStatus !== DocStatus.ReadyToGenerate && (uploadStatus === 'error' ? t('ApplicationDetails.AdvisorySection.GenerateDocument.failed') : <span>{docName}</span>)}
                    </div>

                    <div className="doc-box-inner doc-box-actions">
                        {uploadStatus === DocStatus.ReadyToSign && (
                            <span className={!canESign ? 'eSign-disabled' : ''} tabIndex={0} aria-hidden="true" role="button" onClick={isApplicationClosed ? () => { } : handleDocumentSign}>
                                {' '}
                                {isESignInInProgress ? <Loader /> : (allowSigning ? <Icon type={ICON_PEN_LEFT} size={28} /> : <></>)}
                            </span>
                        )}
                        {uploadStatus === DocStatus.ReadyToSubmit && isCaseDetails && status !== CaseOrderStatus.Submitted && (
                            <span className="ready-to-re-upload" tabIndex={0} aria-hidden="true" role="button" onClick={() => handleDocReUpload(documentId)}>
                                {' '}
                                {isDocReUploadInProgress ? <Loader /> : (<Icon type={ICON_TRY_AGAIN} size={24} />)}
                            </span>
                        )}
                        {
                            !['loading', 'error', 'ready-to-generate', DocStatus.ReadyToUpload, DocStatus.Disabled]?.includes(uploadStatus) && !(type === DocTypes.ID_DOCUMENT && (uploadStatus !== DocStatus.ReadyToSubmit))
                            && (isDownloadInProgress ? <Loader />
                                : (
                                    <span tabIndex={-1} aria-hidden="true" role="button" onClick={disableDocumentDownload ? () => { } : (type === DocTypes.EMPTY_FORM ? onPlanDataChangeFormModalShow : handleDownload)}>
                                        <Icon type={ICON_DOWNLOAD_OUTLINE} size={28} />
                                    </span>
                                )
                            )
                        }
                        {(showUploadIcon)
                            && (
                                <span tabIndex={-2} aria-hidden="true" role="button" onClick={isApplicationClosed ? () => { } : handleReUpload}>
                                    <Icon
                                        type={ICON_DOCUMENT_RE_UPLOAD}
                                        size={28}
                                    />
                                </span>
                            )}
                        {uploadStatus === 'error' && !applicationExpired && type !== DocTypes.ADVISORY && (
                            <Button type={BUTTON_TYPE_DANGER} onClick={handleGenerateDocument} disabled={isDocGenerationInProgress}>
                                <Icon type={ICON_TRY_AGAIN} size={24} />
                                {t('ApplicationDetails.AdvisorySection.GenerateDocument.TryAgain')}
                            </Button>
                        )}
                        {uploadStatus === DocStatus.ReadyToGenerate && !applicationExpired && (
                            <Button type={BUTTON_TYPE_SECONDARY} onClick={isApplicationClosed ? () => { } : handleGenerateDocument} disabled={isApplicationClosed || isDocGenerationInProgress}>
                                {t('ApplicationDetails.AdvisorySection.GenerateDocument.Generate')}
                            </Button>
                        )}
                    </div>
                </div>
                {isSentCase && (
                    <div
                        className="single-application--confirm-document-submission-checkbox"
                        role="button"
                        tabIndex={0}
                        onClick={tryToAcceptDocSend}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                tryToAcceptDocSend(e);
                            }
                        }}
                    >
                        {typeof isSent === 'boolean' && (
                            <Checkbox checked={isSentCheckValue} label={t('checkbox.TranfserFormSent')} />
                        )}
                    </div>
                )}

            </div>
            {showUploadModal && (
                <Modal
                    title=""
                    visible
                    okText={isFollowUpInvestorProfile ? t('Modal.OkText.PleaseConfirm') : t('ApplicationDetails.AdvisorySection.UploadDocument')}
                    cancelText={t('documentSign.modal.documentIsNotSent.cancel')}
                    onOk={type === DocTypes.ADVISORY ? handleAdvisoryDocReUpload : handleDocumentUpload}
                    okButtonProps={{
                        disabled: isUploadInProgress || (isFollowUpInvestorProfile && !investorProfileChecked),
                        loading: isUploadInProgress,
                    }}
                    okType="primary"
                    cancelButtonProps={{ disabled: isUploadInProgress }}
                    onCancel={() => setShowUploadModal(false)}
                    width={610}
                    className="upload-document-modal"
                    borderRadius
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16} />}
                >
                    <Title type={2} className="font-normal mb-32">

                        {type === DocTypes.ID_DOCUMENT ? t('clientOverview.personalDocuments.modalTitle')
                            : (
                                <>
                                    {t('ApplicationDetails.AdvisorySection.UploadDocument')}
                                    {' '}
                                    {docName}
                                </>
                            )}

                    </Title>
                    {type === DocTypes.PROOF_OF_ASSETS ? (
                        <>
                            <Title type={3}>
                                {t('ApplicationDetails.AdvisorySection.RequiredDocuments')}
                            </Title>
                            <ul className="mt-12">
                                {originOfAsset ? kycProofOfAssetsDocNames?.[originOfAsset]?.map((asset: string, index: number) => <li key={`${asset}-${index}`}>{asset}</li>) : ''}
                            </ul>
                        </>
                    ) : ''}

                    {type === DocTypes.ID_DOCUMENT && (
                        <Title type={3} className="font-normal">
                            {t('3b.kyc.allowedFormats')}
                        </Title>
                    )}
                    <UploadDocuments
                        onSuccess={setFiles}
                        onError={setError}
                        numberOfFiles={type === DocTypes.ID_DOCUMENT ? 2 : 1}
                        fileTypesAllowed={allowedFileTypes}
                    />
                    {isFollowUpInvestorProfile && (
                        <div className="investor-profile-checkbox">
                            <label htmlFor="investor-profile-checkbox" className={files?.length < 1 ? 'checkbox-wrapper-disabled' : ''}>{t('ApplicationDetails.DocBox.FollowUpInvestorProfileCheckBox.Label.One')}</label>
                            <Checkbox onChange={handleInvestorProfileCheckbox} id="investor-profile-checkbox" disabled={files?.length < 1} checked={false} label={t('ApplicationDetails.DocBox.FollowUpInvestorProfileCheckBox.Label.Two')} />
                        </div>
                    )}
                    {error && <Error>{error}</Error>}
                </Modal>
            )}
            {showInvestorProfileFakeApplicationModal && (
                <Modal
                    title=""
                    visible
                    okText={t('clientDashboard.close')}
                    cancelText=""
                    onOk={() => setShowInvestorProfileFakeApplicationModal(false)}
                    onCancel={() => setShowInvestorProfileFakeApplicationModal(false)}
                    okType="primary"
                    width={610}
                    className="upload-document-modal"
                    borderRadius
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16} />}
                >
                    <Title type={2} className="font-normal mb-32">
                        {t('ApplicationDetails.DocBox.FollowUpInvestorProfile.Modal.ThanksForSubmitting.Title')}
                    </Title>
                    <p>{t('ApplicationDetails.DocBox.FollowUpInvestorProfile.Modal.ThanksForSubmitting.Text.One')}</p>
                </Modal>
            )}
        </>
    );
};

export default DocBox;
